import type { BranchRepresentationType } from '@readme/api/src/mappings/branch/types';

import React, { useEffect, useState } from 'react';

import useClassy from '@core/hooks/useClassy';

import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Graphic from '@ui/Graphic';
import Icon from '@ui/Icon';
import LazyTippy from '@ui/LazyTippy';
import Menu, { MenuItem, MenuDivider } from '@ui/Menu';
import Timestamp from '@ui/Timestamp';

import { useDeleteBranchModalContext } from '../Modals/DeleteBranch';
import { useMergeBranchModalContext } from '../Modals/MergeBranch';

import DeleteBranchMenuItem from './DeleteBranch';
import styles from './index.module.scss';
import MergeBranchMenuItem from './MergeBranch';

function EditBranchMenu({ branch }: Pick<EditBranchProps, 'branch'>) {
  const bem = useClassy(styles, 'EditBranchMenu');

  return (
    <Menu data-testid="EditBranchMenu" theme="dark">
      <MergeBranchMenuItem branch={branch} />

      <MenuDivider />
      {/** TODO: uncomment once we start working on rename
        * https://linear.app/readme-io/issue/RM-12389/rename-branch
      <MenuItem aria-label="Rename Branch" icon={<Icon aria-hidden="true" name="edit-2" />} TagName="button">
        Rename
      </MenuItem>
        */}
      <DeleteBranchMenuItem branch={branch} />

      {/* TODO: add ExportBranch https://linear.app/readme-io/issue/RM-12781/ui-add-exportbranch-to-editbranch-component */}
      <MenuDivider />
      <MenuItem
        description={
          <Flex gap="xs" layout={branch.href.external.view ? 'col' : 'row'}>
            {!!branch.href.external.view && (
              <Flex
                align="center"
                className={bem('-viewUrl')}
                gap="xs"
                href={branch.href.external.view}
                rel="noopener noreferrer"
                tag="a"
                target="_blank"
              >
                <Graphic name="github" />
                View in GitHub
                <Icon aria-label="Opens in a new tab" className={bem('-viewUrl-icon')} name="arrow-up-right" />
              </Flex>
            )}
            <div>
              Updated <Timestamp value={new Date(branch.updated_at)} />
            </div>
          </Flex>
        }
        focusable={false}
      ></MenuItem>
    </Menu>
  );
}

interface EditBranchProps {
  /** Branch that is being edited. */
  branch: BranchRepresentationType;
  className?: string;
}

/**
 * Renders the "edit" button that opens up a menu to edit a git branch.
 */
export default function EditBranch({ branch, className }: EditBranchProps) {
  const [visible, setVisible] = useState(false);
  const { branchToMerge } = useMergeBranchModalContext();
  const { branchToDelete } = useDeleteBranchModalContext();

  const isActionPending = !!(branchToMerge || branchToDelete);

  // Close menu when merge operation begins.
  useEffect(() => {
    if (isActionPending) {
      setVisible(false);
    }
  }, [isActionPending]);

  return (
    <LazyTippy
      appendTo={() => document.body}
      arrow={false}
      content={<EditBranchMenu branch={branch} />}
      interactive
      maxWidth={320}
      offset={[0, 2]}
      onClickOutside={() => {
        setVisible(false);
      }}
      placement="bottom-end"
      role="dialog"
      visible={visible}
      zIndex={10002} // Must be less than our Modal z-index.
    >
      <Button
        aria-label="Edit Branch"
        className={className}
        kind="secondary"
        onClick={() => setVisible(true)}
        size="xs"
        text
      >
        <Icon aria-hidden="true" name="more-vertical" />
      </Button>
    </LazyTippy>
  );
}
